import * as React from "react";
import { connect } from "react-redux";
import { ApplicationState } from "store";
import { actionCreators, getCalendar } from "page/calendar/store";
import { useState, useEffect } from "react";
import { ModuleInformation } from "burde";
import ModulePicker from "layout/common/controls-area/module-selector/module-picker/module-picker";
import * as ReactCSSTransitionReplace from "react-css-transition-replace";
import { ModuleOption, ModuleResource } from "ui/constants";
import { Spinner } from "common/spinner/spinner";
import * as styles from "./module-selector.css";

type TabItem = {
    slotId: number;
    name: string;
};

type ModuleSelectorProps = {
    addModule: (module: ModuleInformation) => void;
    removeModule: (slotid: number) => void;
    getModules: (
        culture: string,
        calendarSize: string,
        calendarType: string,
    ) => Promise<Array<ModuleResource> | null>;
    culture: string;
    selectedModules: Array<ModuleInformation>;
    maxModules: number;
    calendarSize: string;
    calendarType: string;
} & typeof actionCreators;

const ModuleSelector: React.FC<ModuleSelectorProps> = ({
    culture,
    addModule,
    removeModule,
    getModules,
    selectedModules,
    maxModules,
    calendarSize,
    calendarType,
}) => {
    const [slotId, setSlotId] = useState(0);
    const [isReverseAnimation, setIsReverseAnimation] = useState(false);
    const [moduleResources, setModuleResources] = useState<
        Array<ModuleResource>
    >([]);
    const [
        selectedResourceGroup,
        setSelectedResourceGroup,
    ] = useState<ModuleResource | null>(null);

    useEffect(() => {
        let isMounted = true;
        const fetchModuleResources = async () => {
            const moduleResponse = await getModules(
                culture,
                calendarSize,
                calendarType,
            );
            if (moduleResponse !== null && isMounted) {
                setModuleResources(moduleResponse);

                if (moduleResponse.length > 0) {
                    setSelectedResourceGroup(moduleResponse[0]);
                }
            }
        };

        fetchModuleResources();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        const selectedModule = getSelectedModule(slotId, selectedModules);

        if (selectedModule !== null) {
            const resourceGroup = getModuleResourceGroup(
                selectedModule,
                moduleResources,
            );

            if (resourceGroup !== null) {
                setSelectedResourceGroup(resourceGroup);
            }
        }
    }, [slotId]);

    const getSelectedModule = (
        slotId: number,
        selectedModules: Array<ModuleInformation>,
    ) => {
        const module = selectedModules.find((d) => d.slotId === slotId);

        if (module !== undefined) {
            return module;
        }

        return null;
    };

    const getModuleResourceGroup = (
        module: ModuleInformation,
        moduleResources: Array<ModuleResource>,
    ) => {
        for (let i = 0; i < moduleResources.length; i++) {
            const result = moduleResources[i].modules.find(
                (m) => m.id == module.id,
            );
            if (result !== undefined) {
                return moduleResources[i];
            }
        }

        return null;
    };

    const toggleModule = (module: ModuleOption) => {
        let isSelected = selectedModules.find(
            (d) => d.id === module.id && d.slotId === slotId,
        );

        if (isSelected) {
            removeModule(slotId);
        } else {
            const moduleInformation: ModuleInformation = {
                slotId: slotId,
                id: module.id,
            };

            addModule(moduleInformation);
        }
    };

    const getTabs = () => {
        const tabsArray: Array<TabItem> = [];

        for (let i = 0; i < maxModules; i++) {
            tabsArray.push({ name: (i + 1).toString(), slotId: i });
        }

        return tabsArray;
    };

    if (moduleResources.length == 0) {
        return (
            <div>
                <Spinner></Spinner>
            </div>
        );
    }

    return (
        <div>
            <ul className={styles.tabGroup}>
                {getTabs().map((item, index) => (
                    <li key={"tab" + index} className={styles.tabGroupLi}>
                        <a
                            className={`${styles.categoryTabItem} 
                                 ${
                                     slotId == item.slotId
                                         ? styles.categoryTabItemSelected
                                         : ""
                                 } ${styles.tabItem}`}
                            onClick={() => {
                                setIsReverseAnimation(slotId > item.slotId);
                                setSlotId(item.slotId);
                            }}>
                            {item.name}
                        </a>
                    </li>
                ))}
            </ul>

            <ul className={styles.tabGroup}>
                {moduleResources.length > 1 &&
                    moduleResources.map((item, index) => (
                        <li
                            className={styles.categoryLi}
                            key={item.resourceGroupTitle}>
                            <a
                                className={`${styles.categoryTabItem} 
                                 ${
                                     selectedResourceGroup !== null &&
                                     selectedResourceGroup.resourceGroupTitle ==
                                         item.resourceGroupTitle
                                         ? styles.categoryTabItemSelected
                                         : ""
                                 }`}
                                onClick={() =>
                                    setSelectedResourceGroup(
                                        moduleResources[index],
                                    )
                                }>
                                {item.resourceGroupTitle}
                            </a>
                        </li>
                    ))}
            </ul>

            {selectedResourceGroup !== null && (
                <ReactCSSTransitionReplace
                    transitionName={
                        isReverseAnimation
                            ? "carousel-reverse-swap"
                            : "carousel-swap"
                    }
                    transitionEnterTimeout={300}
                    transitionLeaveTimeout={300}>
                    <ModulePicker
                        key={`${slotId}`}
                        onClick={toggleModule}
                        modules={selectedResourceGroup.modules}
                        selectedModule={getSelectedModule(
                            slotId,
                            selectedModules,
                        )}
                    />
                </ReactCSSTransitionReplace>
            )}
        </div>
    );
};

const mapStateToProps = (state: ApplicationState) => ({
    calendar: getCalendar(state),
});

const mapDispatchToProps = {
    ...actionCreators,
};

export default connect(mapStateToProps, mapDispatchToProps)(ModuleSelector);
